import type { Ref } from "react";
import type { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import type { ImageInputProps } from "@/components/ImageInput/ImageInput";
import { ImageInput } from "@/components/ImageInput/ImageInput";
import { mergeRefs } from "@/helpers/mergeRefs";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormImageInputProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<ImageInputProps, "className" | "onChange" | "value"> {
  name: TName;
  rules?: RegisterOptions<TFormValues, TName>;
  "data-testid"?: string;
  initialFileUrl?: string;
  nOfImages?: number;
  variant?: ImageInputProps["variant"];
  texts?: { add: string };
  isAvatarInput?: boolean;
  layer?: "raised";
  ref?: Ref<HTMLInputElement>;
}

export function FormImageInput<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  "data-testid": dataTestid,
  name,
  rules,
  ref,
  layer,
  ...props
}: FormImageInputProps<TFormValues, TName>): React.ReactNode {
  const {
    field,
    fieldState: { error },
  } = useController<TFormValues, TName>({
    name,
    rules,
    defaultValue: [] as any,
  });

  return (
    <FormErrorWrapper<TFormValues> name={name} layer={layer}>
      <ImageInput
        {...props}
        {...field}
        ref={mergeRefs(field.ref, ref)}
        aria-invalid={error != null}
        value={field.value}
        onChange={field.onChange}
        onBlur={() => {
          field.onBlur();
          props.onBlur?.();
        }}
      />
    </FormErrorWrapper>
  );
}
